import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({ providedIn: 'root' })
export class ToastService {
  private toastCtrl = inject(ToastController);
  private transloco = inject(TranslocoService);
  private activeRateLimitToast?: HTMLIonToastElement;

  async show(message: string, type: 'info' | 'success' | 'error' = 'info', duration = 4000) {
    const color = type === 'error' ? 'danger' : type === 'success' ? 'secondary' : 'primary';
    const icon = type === 'success' ? '✅ ' : type === 'error' ? '❌ ' : 'ℹ️ ';
    const toast = await this.toastCtrl.create({
      message: icon + message,
      duration,
      position: 'top',
      color,
      cssClass: 'custom-toast',
      buttons: [{ icon: 'close', role: 'cancel' }]
    });
    await toast.present();
  }

  /** Displays a rate-limit countdown that updates every second.
   *  @param seconds initial remaining seconds
   */
  async showRateLimitCountdown(seconds: number) {
    if (this.activeRateLimitToast) {
      await this.activeRateLimitToast.dismiss();
    }

    let remaining = seconds;
    const initialMessage = this.transloco.translate('errors.rateLimitMessage', { seconds: remaining });
    const toast = await this.toastCtrl.create({
      message: initialMessage,
      position: 'top',
      color: 'danger',
      cssClass: 'custom-toast',
      buttons: []
    });
    await toast.present();
    this.activeRateLimitToast = toast;

    const interval = setInterval(async () => {
      remaining--;
      if (remaining <= 0) {
        clearInterval(interval);
        if (this.activeRateLimitToast === toast) {
          this.activeRateLimitToast = undefined;
        }
        await toast.dismiss();
        return;
      }
      toast.message = this.transloco.translate('errors.rateLimitMessage', { seconds: remaining });
    }, 1000);
  }

  /**
   * Universal backend error -> translated user-friendly message.
   * Relies on the global `errors.backend.*` dictionary.
   */
  mapBackendError(error: any, fallbackKey: string = 'errors.generic'): string {
    // 1. Network / connection loss
    if (error instanceof TypeError && error.message?.toLowerCase().includes('failed to fetch')) {
      return this.transloco.translate('errors.cloudConnection');
    }
    
    // 2. Universal Backend Error Code Mapping
    const code = error?.error?.error_code;
    if (code && typeof code === 'string') {
      const translationKey = `errors.backend.${code}`;
      const translated = this.transloco.translate(translationKey);
      // Transloco returns the key itself if no translation is found
      if (translated !== translationKey) {
        return translated;
      }
    }

    // 3. Fallback to explicit raw message from backend (if provided)
    const rawBackendMessage = error?.error?.error; 
    if (rawBackendMessage && typeof rawBackendMessage === 'string') {
      return rawBackendMessage;
    }
    
    // 4. Fallback to context-specific key or generic error.
    // We intentionally DO NOT use error.message here to prevent leaking 
    // Angular's raw "Http failure response for..." string.
    return this.transloco.translate(fallbackKey);
  }
}
